export type InboxProvider = 'gmail'
export type InboxAccountStatus = 'connected' | 'disconnected' | 'error'

export type SyncInterval = 'manual' | '15min' | 'hourly' | '6h' | 'daily'

export const SYNC_INTERVAL_MINUTES: Record<Exclude<SyncInterval, 'manual'>, number> = {
  '15min': 15,
  hourly: 60,
  '6h': 360,
  daily: 1440,
}

export function intervalToPreset(
  minutes: number | null | undefined,
  enabled: boolean | null | undefined,
): SyncInterval {
  if (!enabled) return 'manual'
  if (minutes === 15) return '15min'
  if (minutes === 60) return 'hourly'
  if (minutes === 360) return '6h'
  return 'daily'
}

export type ClassificationMode = 'ai' | 'keyword' | 'hybrid'
export type OnboardingStatus = 'pending' | 'configured'
export type BackfillStatus = 'none' | 'running' | 'paused' | 'done'
export type BackfillPausedReason = 'cap' | 'quota' | null
export type ImportHorizon = '30d' | '90d' | '12mo' | 'all' | 'custom'
export type ImportScope = 'all' | 'financial_only' | 'keywords'
export type MailSource = 'inbox' | 'inbox_sent' | 'all'

export interface InboxAccount {
  id: string
  provider: InboxProvider
  email: string
  status: InboxAccountStatus
  last_sync_at: string | null
  last_error: string | null
  sync_interval_minutes: number | null
  next_sync_at: string | null
  auto_sync_enabled: boolean
  ingest_mode: 'all' | 'financial_only'
  classification_mode: ClassificationMode
  onboarding_status: OnboardingStatus
  backfill_status: BackfillStatus
  backfill_paused_reason: BackfillPausedReason
  backfill_total: number | null
  backfill_imported: number | null
  backfill_cap: number | null
  backfill_finished_at: string | null
  backfill_query: string | null
  created_at: string
  updated_at: string
}

export interface OnboardingState {
  account_id: string
  onboarding_status: OnboardingStatus
  classification_mode: ClassificationMode
  ingest_mode: 'all' | 'financial_only'
  backfill_status: BackfillStatus
  backfill_paused_reason: BackfillPausedReason
  backfill_total: number | null
  backfill_imported: number | null
  backfill_cap: number | null
  backfill_started_at: string | null
  backfill_finished_at: string | null
}

export interface OnboardingStartPayload {
  horizon: ImportHorizon
  custom_start?: string | null
  custom_end?: string | null
  scope: ImportScope
  source: MailSource
  keywords?: string[]
  classification_mode: ClassificationMode
  sync_interval_minutes: number
  auto_sync_enabled: boolean
}

export interface EstimateResponse {
  estimate: number
  query: string
}

export type ProcessingStatus = 'new' | 'classified' | 'extracted' | 'skipped' | 'error'

export interface InboxEmail {
  id: string
  inbox_account_id: string
  provider_message_id: string
  thread_id: string | null
  subject: string | null
  from_email: string | null
  snippet: string | null
  internal_date: string | null
  system_category: string | null
  custom_category_id: string | null
  processing_status: ProcessingStatus
  created_at: string
  attachments: InboxAttachment[]
  item_id: string | null
  item_status: InboxItemStatus | null
  item_kind: InboxItemKind | null
}

export type InboxCategoryColor =
  | 'info'
  | 'warning'
  | 'brand'
  | 'success'
  | 'secondary'
  | 'primary'

export interface InboxCategory {
  id: string
  slug: string
  name: string
  is_system: boolean
  is_financial: boolean
  color: InboxCategoryColor
  icon: string
  sort_order: number
  created_at: string
  updated_at: string
}

export interface InboxCategoryCount {
  category_id: string
  slug: string
  name: string
  color: InboxCategoryColor
  icon: string
  is_system: boolean
  is_financial: boolean
  sort_order: number
  count: number
}

export interface CategoryCountsResponse {
  counts: InboxCategoryCount[]
  total: number
}

export interface InboxWatchRule {
  id: string
  name: string
  is_active: boolean
  priority: number
  match_from: string | null
  match_subject: string | null
  match_label: string | null
  match_keywords: string[] | null
  assign_category_id: string | null
  category_name: string | null
  workflow_id: string | null
  workflow_name: string | null
  auto_confirm: boolean
  created_at: string
  updated_at: string
}

export interface WatchRulePayload {
  name: string
  is_active?: boolean
  priority?: number
  match_from?: string | null
  match_subject?: string | null
  match_label?: string | null
  match_keywords?: string[] | null
  assign_category_id?: string | null
  workflow_id?: string | null
  auto_confirm?: boolean
}

export interface CategoryPayload {
  name: string
  color?: InboxCategoryColor
  icon?: string
}

export interface SyncResult {
  ok: boolean
  fetched: number
  saved: number
  classified: number
  extracted: number
  quota_exhausted?: boolean
}

export interface ConnectStartResponse {
  auth_url: string
}

export type InboxItemKind = 'invoice' | 'bill' | 'receipt' | 'statement' | 'other'
export type InboxItemStatus = 'pending' | 'confirmed' | 'dismissed' | 'failed'

export interface InboxAttachment {
  id: string
  filename: string | null
  mime_type: string | null
  size_bytes: number | null
}

export interface InboxItem {
  id: string
  inbox_email_id: string
  kind: InboxItemKind
  vendor_name: string | null
  vendor_email: string | null
  invoice_number: string | null
  invoice_date: string | null
  due_date: string | null
  total_amount: number | string | null
  currency: string | null
  confidence: number | string | null
  status: InboxItemStatus
  error: string | null
  created_at: string
  updated_at: string
  email_subject: string | null
  email_from: string | null
  internal_date: string | null
  system_category: string | null
  custom_category_id: string | null
  attachments: InboxAttachment[]
}

export interface EmailItemSummary {
  id: string
  kind: InboxItemKind
  vendor_name: string | null
  vendor_email: string | null
  invoice_number: string | null
  invoice_date: string | null
  due_date: string | null
  total_amount: number | string | null
  currency: string | null
  confidence: number | string | null
  status: InboxItemStatus
  error: string | null
}

export interface InboxEmailDetail {
  id: string
  inbox_account_id: string
  provider_message_id: string
  thread_id: string | null
  subject: string | null
  from_email: string | null
  snippet: string | null
  internal_date: string | null
  body_text: string | null
  body_html: string | null
  system_category: string | null
  custom_category_id: string | null
  processing_status: ProcessingStatus
  classification_source: string | null
  classification_confidence: number | string | null
  summary: string | null
  attachments: InboxAttachment[]
  item: EmailItemSummary | null
}

export interface AttachmentDownloadResponse {
  url: string
  filename: string
  mime_type: string
}

export interface AttachmentPreviewTextResponse {
  kind: 'pdf' | 'csv' | 'xlsx' | 'text' | 'unsupported'
  text: string
  filename: string
  mime_type: string
}
